import { useEffect, useRef } from "react";
import {
  hashKey,
  type DefaultError,
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useQueryScope } from "@/hooks/use-query-scope";
import type { QueryScope } from "@/queries/scope";

/**
 * Which source answers a domain hook's read.
 *
 * - `"cache"`: the hook's kill switch is on (or it has none) and the session
 *   has said who is signed in, so the read goes through Query and `query` is
 *   live.
 * - `"store"`: the hook's kill switch is off, so the read is the store's, as
 *   before. The hook calls `whenOff`, and reads its answer from the store.
 *   Never the mode of a hook without a kill switch.
 * - `"waiting"`: the read would go through Query but the session has not
 *   resolved, or there is no workspace yet. Nothing is read: neither source
 *   would be keyed right.
 */
export type ScopedQueryMode = "cache" | "store" | "waiting";

export interface ScopedQuery<TData, TError = DefaultError> {
  readonly mode: ScopedQueryMode;
  /** Live in `"cache"` mode; otherwise a disabled query (pending, no data). */
  readonly query: UseQueryResult<TData, TError>;
}

interface ScopedQueryBaseOptions {
  /** `false` reads nothing from either source, e.g. for a viewer who may not see it. */
  readonly enabled?: boolean;
}

/** A domain still read from its store while its kill switch is off. */
interface FlaggedScopedQueryOptions extends ScopedQueryBaseOptions {
  /**
   * The domain's own kill switch, which keeps the store path while it is
   * off. Omitted, Query always answers: the `query-cache` flag that used to
   * be the default is retired (NG-2793).
   */
  readonly flag?: string;
  /**
   * The store's read, run in `"store"` mode while `enabled` and there is a
   * workspace. It runs again whenever the read it stands in for changes (its
   * key does), and the function it returns, if any, cleans up the last run.
   */
  readonly whenOff?: () => void | (() => void);
}

/**
 * A domain whose store server-data code is gone (NG-1043, one PR per domain):
 * Query is its only source, so there is no flag to read and no `whenOff`.
 */
interface QueryOnlyScopedQueryOptions extends ScopedQueryBaseOptions {
  readonly flag: null;
  readonly whenOff?: never;
}

export type ScopedQueryOptions = FlaggedScopedQueryOptions | QueryOnlyScopedQueryOptions;

/** Stands in for the principal while it is unknown; the query is disabled then. */
const NO_PRINCIPAL = "";

/** The key `useScopedMode` reads for a domain with no kill switch: no flag has it. */
const NO_KILL_SWITCH = "";

/**
 * The shared part of every domain hook (contract §9): the scope, the flag
 * switch and the flag-off read. A domain hook is then the query definition and
 * its answer. Once a domain's PR has deleted its store server-data code, the
 * hook passes `flag: null` and Query is its only source: `mode` is `"cache"`
 * or `"waiting"`, and there is no `whenOff`:
 *
 *     export function useMemberBank(slug: string) {
 *       const { workspaceService } = useStore();
 *       const { query } = useScopedQuery(
 *         slug,
 *         (scope) => memberBankQuery(workspaceService, scope),
 *         { flag: null },
 *       );
 *       return query.data ?? [];
 *     }
 *
 * A domain still read from its store with the flag off (one behind
 * `query-cache`, or a high-risk one behind its own kill switch, passed as
 * `flag`) also gives the store's read as `whenOff`, and answers from the store
 * in `"store"` mode:
 *
 *     const { mode, query } = useScopedQuery(
 *       slug,
 *       (scope) => requestsQuery(intakeService, scope),
 *       { whenOff: () => void requests.ensure(slug) },
 *     );
 *     return mode === "cache" ? (query.data ?? []) : requests.items;
 *
 * The flag switch lives here and nowhere else, so a component that moves onto
 * a domain hook moves once. With the flag on it is a real observer: an
 * `invalidateQueries` from a writer, or an NG-2666 hint, re-reads what it
 * shows. A hint for a hook-read domain is an `observedRead` row in
 * `stores/cache-hints/<domain>.ts`: `mark` invalidates with
 * `refetchType: "none"` and `reread` refetches the active, stale entries,
 * never `invalidateScoped`.
 *
 * `makeQuery` builds the definition from `app/queries`. It is called on every
 * render, also when the read is off (with a placeholder principal), so it must
 * be a pure options builder.
 *
 * Reads the flag and the signed-in user from stores, so the calling component
 * must be an `observer`.
 */
export function useScopedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  tenant: string | undefined,
  makeQuery: (scope: QueryScope) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  { enabled = true, whenOff, flag = null }: ScopedQueryOptions = {},
): ScopedQuery<TData, TError> {
  const { mode, scope } = useScopedMode(tenant, flag);
  const options = makeQuery(scope);
  const query = useQuery({
    ...options,
    enabled: mode === "cache" && enabled ? (options.enabled ?? true) : false,
  });
  useWhenOff(mode, tenant, options.queryKey, { enabled, whenOff });

  return { mode, query };
}

/**
 * Which source answers, and the scope to build the query with: the signed-in
 * scope in `"cache"` mode, otherwise a placeholder whose query stays disabled.
 * `flag` is the switch onto Query; `null` means Query always answers. Shared by
 * `useScopedQuery` and `useScopedInfiniteQuery`, so the flag switch still lives
 * in one place.
 */
export function useScopedMode(
  tenant: string | undefined,
  flag: string | null,
): {
  mode: ScopedQueryMode;
  scope: QueryScope;
} {
  // Read unconditionally (a hook); a domain without a kill switch reads the
  // sentinel and ignores the answer.
  const flagOn = useFeatureFlag(flag ?? NO_KILL_SWITCH);
  const cacheOn = flag === null || flagOn;
  const scope = useQueryScope(cacheOn ? tenant : undefined);
  const mode: ScopedQueryMode = !cacheOn ? "store" : scope ? "cache" : "waiting";
  return { mode, scope: scope ?? { tenant: tenant ?? "", principal: NO_PRINCIPAL } };
}

/**
 * Run the store's read in `"store"` mode while `enabled` and there is a
 * workspace, again whenever the read it stands in for changes (`queryKey`
 * does), cleaning up the last run. Shared by both scoped hooks.
 */
export function useWhenOff(
  mode: ScopedQueryMode,
  tenant: string | undefined,
  queryKey: QueryKey,
  { enabled = true, whenOff }: { enabled?: boolean; whenOff?: () => void | (() => void) },
): void {
  const whenOffRef = useRef(whenOff);
  useEffect(() => {
    whenOffRef.current = whenOff;
  });
  // The key with the placeholder principal names the read the store stands in for.
  const readHash = hashKey(queryKey);
  useEffect(() => {
    if (mode !== "store" || !enabled || !tenant) return;
    return whenOffRef.current?.();
  }, [mode, enabled, tenant, readHash]);
}
